import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../api.service';

@Component({
  selector: 'app-invitation',
  templateUrl: './invitation.component.html',
  styleUrls: ['./invitation.component.css']
})
export class InvitationComponent implements OnInit {

  inviteForm!: FormGroup;
  canGenerate = true;
  loading = false;
  
  //  Added properties
  currentToken: string | null = null;
  inviteLink: string | null = null;
  expiresAt: string | null = null;
  hasActiveToken: boolean = false;
  invitationsSent: number = 0;
  invitationsAccepted: number = 0;

  constructor(
    private fb: FormBuilder,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      friend_email: ['', [Validators.email]],
      invite_link: [{value: '', disabled: true}],
      expires_at: [{value: '', disabled: true}]
    });

    this.loadActiveInvite();
  }

  //  Load active invite on page load
  loadActiveInvite(): void {
    this.api.get('invite/active').subscribe({
      next: (res: any) => {
        console.log('Active invite response:', res);
        
        if (res.status && res.has_active_token) {
          // Show existing invite
          this.currentToken = res.token;
          this.inviteLink = res.invite_link;
          this.expiresAt = res.expires_at;
          this.hasActiveToken = true;
          this.invitationsSent = res.invitations_sent || 0;
          this.invitationsAccepted = res.invitations_accepted || 0;
          
          // Update form
          this.inviteForm.patchValue({
            invite_link: res.invite_link,
            expires_at: res.expires_at
          });
          
          this.canGenerate = false;
        } else {
          // No active token
          this.hasActiveToken = false;
          this.currentToken = null;
          this.inviteLink = null;
          this.expiresAt = null;
          this.canGenerate = true;
        }
      },
      error: (err) => {
        console.error('Error loading active invite:', err);
        this.hasActiveToken = false;
      }
    });
  }

  //  Generate new invite
  generateInvite(): void {
    if (!this.canGenerate) return;
    
    this.loading = true;

    const payload = {
      email: this.inviteForm.value.friend_email || null
    };

    console.log('Generating invite with payload:', payload);

    this.api.post('invite/generate', payload).subscribe({
      next: (res: any) => {
        console.log('Generate response:', res);
        
        if (res.status) {
          this.currentToken = res.token;
          this.inviteLink = res.invite_link;
          this.expiresAt = res.expires_at;
          this.hasActiveToken = true;
          
          this.inviteForm.patchValue({
            invite_link: res.invite_link,
            expires_at: res.expires_at
          });

          this.canGenerate = false;
          
          // Clear email field if it was just for generation
          if (!this.inviteForm.value.friend_email) {
            this.inviteForm.patchValue({ friend_email: '' });
          }
        }
        

      this.loading = false;

      },
      error: (err) => {
        console.error('Generate error:', err);
        alert('Failed to generate invite: ' + (err.error?.message || 'Unknown error'));
        this.loading = false;
      }
    });
  }

  //  Copy shareable link
  copyLink(): void {
    if (!this.inviteLink) {
      alert('No invite link available. Generate one first.');
      return;
    }

    navigator.clipboard.writeText(this.inviteLink).then(() => {
      alert('Invite link copied to clipboard!');
    }).catch(err => {
      console.error('Copy failed:', err);
      alert('Failed to copy link');
    });
  }

//  Send invite via email
sendInvite(): void {
  const email = this.inviteForm.value.friend_email;
  const token = this.currentToken;
  const inviteLink = this.inviteLink;
  
  if (!email) {
    alert('Please enter email address');
    return;
  }
  
  if (!token && !inviteLink) {
    alert('No invite token or link available');
    return;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    alert('Please enter a valid email address');
    return;
  }

  this.loading = true;

  //  Send both token and invite_link (Laravel will handle either)
  const payload = {
    email: email,
    token: token,           // Send token if available
    invite_link: inviteLink // Send link if available
  };

  console.log('Sending invite - Payload:', payload);

  this.api.post('invite/send-email', payload).subscribe({
    next: (res: any) => {
      console.log('Send email response:', res);
      
      if (res.status) {
        alert(' Invitation sent successfully!\n' + 
              `Monthly used: ${res.data.monthly_used}/10\n` +
              `Expires: ${new Date(res.data.expires_at).toLocaleDateString()}`);
        
        // Clear email field
        this.inviteForm.patchValue({ friend_email: '' });
        
        // Refresh data
        this.loadActiveInvite();
      } else {
        alert('❌ ' + (res.message || 'Failed to send invite'));
      }
      
      this.loading = false;
    },
    error: (err) => {
      console.error('Send email error:', err);
      
      if (err.status === 422) {
        // Laravel validation errors
        const errors = err.error.errors;
        let errorMsg = 'Validation errors:\n';
        Object.keys(errors).forEach(key => {
          errorMsg += `• ${errors[key].join(', ')}\n`;
        });
        alert(errorMsg);
      } else if (err.error?.message) {
        alert('Error: ' + err.error.message);
      } else {
        alert('Failed to send invite: ' + err.message);
      }
      
      this.loading = false;
    }
  });
}

  //  Generate new invite (when current one is used/expired)
  generateNewInvite(): void {
    this.canGenerate = true;
    this.generateInvite();
  }

  //  Get days remaining
  getDaysRemaining(): string {
    if (!this.expiresAt) return '';
    
    const expiry = new Date(this.expiresAt);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? `${diffDays} days` : 'Expired';
  }
}